import React from 'react';
import { images } from '../constants/images';
import { Page, TranslationKey } from './pages-types';
import { PageRoutes } from './page-routes';
import { useTranslation, Trans } from 'react-i18next';
import {
  paushPurnimaPage,
  makarSankrantiPage,
  mauniAmavasyaPage,
  basantPanchamiPage,
  maghiPurnimaPage,
  mahaShivratriPage,
} from './page-magh-mela.bathing-dates';
import { Link } from '@remix-run/react';

// Who We Are content component
const WhoWeAreContent: React.FC = () => {
  return (
    <div className="space-y-6">
      <Trans i18nKey="common:about-us.who-we-are.detail">
        <p>
          Kumbh Sukrit has been engaged in Kumbh for over a decade: offered by{' '}
          <a
            href="https://sukritwellness.com"
            target="_blank"
            rel="noreferrer"
            className="text-emerald-600 hover:text-emerald-700 underline underline-offset-2 font-medium"
          >
            Sukrit Wellness Tours
          </a>
          . Having hosted 10,000+ guests from over 25 countries from around the world, we
          are a team of professionals from across various industries, with a passion for
          (1) the traditions of Ancient India, and (2) high-quality service.
        </p>
        <p>
          Over the last decade, this has taken us on a unique journey through the
          incredible heritage and uniqueness of the Magh Mela.
        </p>
        <p>
          In 2019, we hosted thousands of guests from around the globe in a tent village
          in the famed <strong className="text-emerald-800">Sanskriti Vann</strong>, where
          ancient Rishis and Yogis meditated for years and decades. The sanctity of this
          forest and feedback we received from those who stayed in it during Kumbh 2019
          urged us to ask: How can we offer our guests an evermore untouched experience of
          Ancient India and the traditions it represents?
        </p>
        <p>
          In 2025 at the Maha Kumbh Mela, we hosted thousands of guests from around the
          globe in a newly created eco-village of Mudhouses. We are so excited to be back
          again, now, for the Magh Mela 2026 - offering not only our Premium Mudhouse
          suites - but also, Premium Twin tents and a Mudhouse dormitory.
        </p>
      </Trans>
    </div>
  );
};

// Who We Are page configuration
export const whoWeArePage: Page = {
  title: 'common:about-us.who-we-are.title',
  subtitle: 'common:about-us.who-we-are.summary',
  path: PageRoutes.whoWeAre,
  heroImage: images['who-are-we-hero-v2'],
  heroImageAlt: 'common:about-us.who-we-are.title',
  readingTime: 5,
  content: <WhoWeAreContent />,
  linkedPages: (s) => [
    s.getPageOrThrow(PageRoutes.paushPurnima),
    s.getPageOrThrow(PageRoutes.mudhouse),
  ],
};

interface ChildPagePreviewProps {
  path: string;
  readingTime: number;
  title: TranslationKey;
  subtitle?: TranslationKey;
  heroImage?: string;
}

const ChildPagePreview: React.FC<ChildPagePreviewProps> = ({
  path,
  readingTime,
  title,
  subtitle,
  heroImage,
}) => {
  const { t } = useTranslation(['home', 'common']);

  return (
    <article className="group">
      <Link prefetch="intent" to={path}>
        <div className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 h-full flex flex-col border border-gray-100 hover:border-emerald-200">
          {heroImage && (
            <div className="h-40 overflow-hidden">
              <img
                src={heroImage}
                alt=""
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
            </div>
          )}
          <div className="p-5 flex flex-col flex-1">
            <h3 className="font-display text-emerald-800 group-hover:text-emerald-600 text-lg font-bold transition-colors">
              {t(title)}
            </h3>
            {subtitle && (
              <p className="mt-2 text-gray-600 text-sm line-clamp-2 flex-1">
                {t(subtitle)}
              </p>
            )}
            <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
              <span className="text-xs text-gray-500 uppercase tracking-wider">
                {readingTime} {t('common:min-read')}
              </span>
              <span className="text-emerald-600 font-semibold text-sm group-hover:translate-x-1 transition-transform">
                Read &rarr;
              </span>
            </div>
          </div>
        </div>
      </Link>
    </article>
  );
};

export const maghMelaPageChildren = [
  whoWeArePage,
  paushPurnimaPage,
  makarSankrantiPage,
  mauniAmavasyaPage,
  basantPanchamiPage,
  maghiPurnimaPage,
  mahaShivratriPage,
];

export const maghMelaPage: Page = {
  title: 'home:accommodation.magh-mela-2026',
  subtitle: 'home:significance.spiritual.title',
  path: PageRoutes.maghMela,
  heroImage: images['who-are-we-hero-v2'],
  heroImageAlt: 'home:significance.pot.title',
  readingTime: maghMelaPageChildren.reduce(
    (acc, { readingTime }) => acc + readingTime,
    0,
  ),
  maxWidth: true,
  content: (
    <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
      {maghMelaPageChildren.map((page, index) => (
        <ChildPagePreview key={index} {...page} />
      ))}
    </div>
  ),
};
